import React, { useEffect, useState } from 'react';
import { Icon } from '../Icon/Icon';
import { ViewportOverlay } from '../Overlay/ViewportOverlay';
import { SetAvatar, SetChip, SetTriState } from '../SettingsShell/controls';
import { planCounts } from './insuranceDerive';
import type { Carrier, PlanStatus } from '../../settings-practice/types';

export interface CarrierPlansModalProps {
  carrier: Carrier | null;
  providerCount: number;
  onClose: () => void;
  onSave: (next: Carrier) => void;
}

const PLAN_OPTIONS: { value: PlanStatus; label: string; tone: 'muted' | 'amber' | 'green' }[] = [
  { value: 'not_taken', label: 'Not taken', tone: 'muted' },
  { value: 'out_of_network', label: 'Out of network', tone: 'amber' },
  { value: 'in_network', label: 'In network', tone: 'green' },
];

/**
 * A carrier's plans (spec 15 §7.4).
 *
 * Three states per plan, never a boolean — a single accepted/not toggle
 * collapses the middle one, which is the one that turns into a complaint.
 *
 * The unlisted block shows the raw name exactly as the payer returned it. Never
 * normalised, never title-cased, never fuzzy-matched onto an existing plan: a
 * wrong match produces a wrong answer about money.
 */
export function CarrierPlansModal({ carrier, providerCount, onClose, onSave }: CarrierPlansModalProps) {
  const [c, setC] = useState<Carrier | null>(carrier);
  useEffect(() => { setC(carrier); }, [carrier]);

  if (!c) {
    return <ViewportOverlay open={false} onClose={onClose}>{null}</ViewportOverlay>;
  }

  const counts = planCounts(c);

  return (
    <ViewportOverlay open={carrier !== null} onClose={onClose}>
      <div
        onClick={(e) => { e.stopPropagation(); }}
        className="flex max-h-[90vh] w-[min(720px,calc(100vw-32px))] flex-col overflow-hidden rounded-[18px] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[0_18px_48px_rgba(36,41,54,.18)]"
      >
        <div className="flex items-start justify-between gap-3 border-b border-[var(--border-default)] px-6 py-5">
          <div className="flex items-center gap-3">
            <SetAvatar initials={c.initials} colour={c.colour} size={44} />
            <div>
              <h2 className="m-0 text-[18px] font-bold tracking-[-0.02em] text-[var(--text-primary)]">{c.name}</h2>
              <p className="m-0 mt-0.5 text-[12.5px] text-[var(--text-muted)]">
                Payer {c.payerId} ·{' '}
                <strong className="text-[var(--text-secondary)]">
                  {counts.taken} of {counts.total} plans taken
                </strong>
                , {counts.inNetwork} in network
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-[10px] border border-[var(--border-default)] text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]"
          >
            <Icon kind="x" size={14} stroke={2} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="bg-[var(--brand-indigo-tint)] px-6 py-4 text-[12.5px] leading-[1.65] text-[var(--text-secondary)]">
            <strong>A patient who says &ldquo;I have {c.aliases[0] ?? c.name}&rdquo; has not told you enough.</strong>{' '}
            On <strong>PPO</strong> they pay the in-network share. On <strong>Premier</strong> they pay more, because
            this practice takes it but is not in network with it. On <strong>DeltaCare</strong> they cannot be seen here
            at all. <strong>Three different answers behind one word</strong>, which is why ELVA asks which plan before it
            says anything about money — and why this screen exists.
          </div>

          {/* aliases */}
          <div className="px-6 py-[18px]" style={{ borderBottom: '1px solid #edf0f6' }}>
            <div className="text-[13px] font-bold text-[var(--text-primary)]">What patients call the carrier</div>
            <p className="m-0 mt-1 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
              How ELVA recognises it when somebody says it out loud.{' '}
              <strong className="text-[var(--text-secondary)]">Add what they actually say</strong>, not what is printed
              on the card — the card is in their bag.
            </p>
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              {c.aliases.map((a) => (
                <SetChip key={a}>{a}</SetChip>
              ))}
              <button type="button" className="text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">
                Add
              </button>
            </div>
          </div>

          {/* plans */}
          <div className="px-6 pt-[18px]">
            <div className="text-[13px] font-bold text-[var(--text-primary)]">Plans under this carrier</div>
            <p className="m-0 mt-1 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
              <strong className="text-[var(--text-secondary)]">Synced from the practice management system</strong>, which
              already holds a carrier-to-plan table because that is how claims get filed.{' '}
              <strong className="text-[var(--text-secondary)]">A contract is signed with a plan, not with a carrier</strong>
              , so each one is set on its own — taking Delta PPO says nothing about Delta Premier.
            </p>
          </div>

          <div className="mt-3 grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_260px] gap-x-4 bg-[var(--surface-sunken)] px-6 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
            <span>Plan</span>
            <span>What patients call it</span>
            <span>This practice</span>
          </div>
          {c.plans.map((p) => (
            <div
              key={p.id}
              className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_260px] items-center gap-x-4 px-6 py-3"
              style={{ borderBottom: '1px solid #edf0f6' }}
            >
              <span className="truncate text-[13.5px] font-semibold text-[var(--text-primary)]">{p.name}</span>
              <span className="flex min-w-0 flex-wrap gap-1.5">
                {p.aliases.map((a) => (
                  <SetChip key={a}>{a}</SetChip>
                ))}
              </span>
              <span>
                <SetTriState
                  name={`plan-${p.id}`}
                  value={p.status}
                  options={PLAN_OPTIONS}
                  onChange={(next) => {
                    setC({ ...c, plans: c.plans.map((x) => (x.id === p.id ? { ...x, status: next } : x)) });
                  }}
                />
              </span>
            </div>
          ))}

          {/* unlisted plans — raw, never normalised */}
          {c.unlisted.length > 0 ? (
            <div className="m-6 rounded-[12px] bg-[#FBF3E4] p-4">
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-bold text-[var(--warn-amber)]">
                  Came back on an eligibility check, not on this list
                </span>
                <span className="rounded-full bg-[#F3E3C4] px-2 py-0.5 text-[11px] font-bold text-[var(--warn-amber)]">
                  {c.unlisted.length}
                </span>
              </div>
              <p className="m-0 mt-1.5 text-[11.5px] leading-[1.6] text-[var(--warn-amber)]">
                The payer named a plan nobody has told ELVA about,{' '}
                <strong>
                  so ELVA had no honest answer for {c.unlisted.reduce((n, u) => n + u.patientCount, 0)} patients
                </strong>{' '}
                — it could not say in network, out of network or not taken. The name is shown exactly as it came back,
                not tidied up.
              </p>
              {c.unlisted.map((u) => (
                <div key={u.id} className="mt-3 flex items-center justify-between gap-4">
                  <span className="min-w-0">
                    <span className="block truncate font-[var(--font-mono)] text-[12.5px] font-semibold text-[var(--text-primary)]">
                      {u.rawName}
                    </span>
                    <span className="block text-[11px] text-[var(--text-muted)]">
                      <strong className="text-[var(--text-secondary)]">{u.patientCount} patients</strong> turned up with
                      it · last {u.lastSeen}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="shrink-0 rounded-[9px] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-1.5 text-[12px] font-semibold text-[var(--brand-indigo)] hover:bg-[var(--surface-sunken)]"
                  >
                    Set status
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          <div className="bg-[var(--surface-sunken)] px-6 py-4 text-[12px] leading-[1.65] text-[var(--text-secondary)]">
            <strong>The carrier&rsquo;s row on the list behind this is worked out from these plans, not set separately.</strong>{' '}
            {c.name} reads <strong>{counts.inNetwork > 0 ? 'In network' : 'Out of network'}</strong> because{' '}
            {counts.inNetwork} of the {counts.taken} plans taken are. Switch those to out and the row turns amber, and
            ELVA starts telling {c.aliases[0] ?? c.name} patients what it will cost them.
          </div>
          <div className="px-6 py-4 text-[12px] leading-[1.65] text-[var(--text-muted)]">
            <strong className="text-[var(--text-secondary)]">
              Per-provider network stays per carrier, not per plan.
            </strong>{' '}
            A practice holds the contract; a provider is credentialed under it. If a provider is in network with{' '}
            {c.name}, they are in network with the {c.name} plans this practice holds — across all {providerCount}{' '}
            providers on the roster.{' '}
            <strong className="text-[var(--text-secondary)]">
              The lossy case is a provider credentialed on PPO but not Premier
            </strong>{' '}
            — rare enough to leave for now, and the honest place to fix it later is this screen, not the provider panel.
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 border-t border-[var(--border-default)] bg-[var(--surface-sunken)] px-6 py-4">
          <span className="text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
            Plans come from the practice management system and from eligibility checks.{' '}
            <strong className="text-[var(--text-secondary)]">
              Nothing here is typed by hand except the names patients use.
            </strong>
          </span>
          <span className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-2 text-[13.5px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-header)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => { onSave(c); }}
              className="rounded-[10px] bg-[var(--brand-indigo)] px-4 py-2 text-[13.5px] font-semibold text-white"
            >
              Save
            </button>
          </span>
        </div>
      </div>
    </ViewportOverlay>
  );
}
